/**
 * Moving a text prop between its stored form (HTML with Twig in it) and the rich
 * editor's form (HTML with atom elements) — losslessly, or not at all.
 *
 * ### The contract
 *
 * `fromEditorHtml(toEditorHtml(v)) === v`, **byte for byte**, for every value the
 * rich editor is allowed to open. Anything that fails it must fail `canEditRichly`
 * instead, and fall back to the plain code field.
 *
 * That is not a nicety. Every keystroke in the editor writes the prop, recompiles and
 * changes the stored body — so an unstable round-trip means *focusing* a field
 * rewrites a template nobody edited, and the revision-keyed write-back cannot help,
 * because a real edit was dispatched.
 *
 * ### Why the gate is the feature
 *
 * A WYSIWYG normalizes what it does not understand: a partner's hand-written table
 * becomes `<p>`s, a Twig `{% if %}` becomes text, an HTML comment holding Twig
 * disappears. Those values exist in this partner's templates today. So the editor
 * opens **only** for content it can represent exactly, and says so when it cannot —
 * rather than opening everything and quietly flattening some of it.
 */

import { EMBEDDED_EXPRESSION } from './twigExpressions';

/** Tags the rich editor can represent and re-emit unchanged. */
const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'a',
  'ul',
  'ol',
  'li',
  'span',
]);

/** Attributes it round-trips. Anything else (class, id, …) fails the gate. */
const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel']),
  span: new Set(['data-cvn-var', 'data-cvn-trans', 'style']),
};

/**
 * The CSS properties a styled `<span>` may carry — the three the toolbar can set.
 *
 * **Why the gate inspects the value and not just the attribute name.** The
 * round-trip assertion below runs `fromEditorHtml(toEditorHtml(value))`, and both
 * are pure string transforms that leave non-Twig markup alone — so *any* span
 * style passes it. The lossy step is TipTap's parse-and-serialize, which this
 * module never runs, and TipTap drops declarations no loaded extension claims.
 * Allowing `style` by name alone would therefore admit exactly the silent
 * flattening the gate exists to prevent: open a span with `letter-spacing`, and
 * focusing the field would rewrite the stored body without it.
 *
 * Keep in step with the marks registered in `richTextExtensions.ts`. A property
 * added here without a mark to hold it is a value the editor will quietly eat.
 */
const ALLOWED_STYLE_PROPERTIES = new Set([
  'color',
  'font-size',
  // A stack, commas and all — never a `;`, or the split below would read it as a
  // second declaration. `EMAIL_SAFE_FONTS` is tested for exactly that.
  'font-family',
]);

/**
 * Every declaration in a style attribute is one the editor can hold.
 *
 * Entities are decoded **before** the split, for two reasons that point the same
 * way: a quoted family name comes back from the DOM as `&quot;`, whose own
 * semicolon would otherwise chop `font-family: &quot;Trebuchet MS&quot;` into
 * nonsense declarations and reject a value the editor itself wrote; and a second
 * declaration hidden as `color:red&#59;display:none` has to be seen, not skipped.
 */
function styleIsRepresentable(declarations: string): boolean {
  return unescapeAttribute(declarations)
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .every((part) => {
      const property = part.slice(0, part.indexOf(':')).trim().toLowerCase();
      return ALLOWED_STYLE_PROPERTIES.has(property);
    });
}

const TAG = /<\s*\/?\s*([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)\/?\s*>/g;
const ATTRIBUTE = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?/g;

/** A Twig statement or comment — control flow the editor must never touch. */
const TWIG_STATEMENT = /\{[%#]/;

export const RICH_TEXT_REJECTION = {
  TWIG_STATEMENT: 'twig-statement',
  HTML_COMMENT: 'html-comment',
  UNSUPPORTED_TAG: 'unsupported-tag',
  UNSUPPORTED_ATTRIBUTE: 'unsupported-attribute',
  NOT_ROUND_TRIPPABLE: 'not-round-trippable',
} as const;

export type RichTextRejection =
  (typeof RICH_TEXT_REJECTION)[keyof typeof RICH_TEXT_REJECTION];

/**
 * Why the rich editor cannot open this value, or null when it can.
 *
 * Returns the reason rather than a boolean so the UI can say *which* thing it cannot
 * show — "this text contains Twig logic" is actionable, "cannot edit" is not.
 */
export function richTextRejection(value: string): RichTextRejection | null {
  if (TWIG_STATEMENT.test(value)) return RICH_TEXT_REJECTION.TWIG_STATEMENT;
  // Comments are where partner templates hide Twig from Outlook; a parse would
  // drop them silently.
  if (value.includes('<!--')) return RICH_TEXT_REJECTION.HTML_COMMENT;

  for (const match of value.matchAll(TAG)) {
    const tag = match[1].toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) return RICH_TEXT_REJECTION.UNSUPPORTED_TAG;

    const allowed = ALLOWED_ATTRIBUTES[tag];
    for (const attribute of match[2].matchAll(ATTRIBUTE)) {
      const name = attribute[1].toLowerCase();
      if (!allowed?.has(name)) return RICH_TEXT_REJECTION.UNSUPPORTED_ATTRIBUTE;

      if (name === 'style') {
        const value = attribute[0].slice(attribute[0].indexOf('=') + 1).trim();
        if (!styleIsRepresentable(value.replace(/^['"]|['"]$/g, ''))) {
          return RICH_TEXT_REJECTION.UNSUPPORTED_ATTRIBUTE;
        }
      }
    }
  }

  // The contract, checked rather than assumed: if this value does not survive its
  // own round-trip, the editor must not open it whatever the tags say.
  if (fromEditorHtml(toEditorHtml(value)) !== value) {
    return RICH_TEXT_REJECTION.NOT_ROUND_TRIPPABLE;
  }

  return null;
}

export function canEditRichly(value: string): boolean {
  return richTextRejection(value) === null;
}

/** Attribute-safe text (the atoms' payloads are Twig, which can contain quotes). */
function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function unescapeAttribute(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

/** `{{ 'group.key'|trans }}` — the whole expression, so the atom keeps the filter. */
const TRANS_EXPRESSION = /\{\{\s*(['"])([^'"\n]+)\1\s*\|\s*trans\s*(\|\s*raw\s*)?\}\}/g;

/**
 * The stored value as HTML the editor can parse: Twig expressions become atom
 * elements, everything else is untouched.
 *
 * Translations are matched **first** so `{{ 'a.b'|trans }}` becomes a translation atom
 * rather than a variable atom holding a filter expression — the two are different
 * things to a user, and the dropdowns that insert them are different dropdowns.
 */
export function toEditorHtml(value: string): string {
  return value
    .replace(
      TRANS_EXPRESSION,
      (_match, _quote, key: string, raw?: string) =>
        `<span data-cvn-trans="${escapeAttribute(key)}${raw ? '|raw' : ''}"></span>`,
    )
    .replace(EMBEDDED_EXPRESSION, (match, expression: string) => {
      const trimmed = expression.trim();
      // `{{ }}` with nothing in it is text, not an expression.
      if (!trimmed) return match;
      return `<span data-cvn-var="${escapeAttribute(trimmed)}"></span>`;
    });
}

/** Matches one atom span, self-closing or paired, with nothing inside. */
const ATOM_SPAN =
  /<span\s+data-cvn-(var|trans)="([^"]*)"\s*(?:\/>|>\s*<\/span>)/g;

/** A document that is exactly one paragraph, captured without its wrapper. */
const SINGLE_PARAGRAPH = /^<p>([\s\S]*)<\/p>$/;

/**
 * The editor's HTML back to a stored value.
 *
 * **A single paragraph loses its wrapper.** These props are interpolated into a
 * component's own styled container (`{{ html|raw }}` inside a `<td>`), so a `<p>` the
 * author never wrote would add margins the design did not ask for — and would make the
 * value differ from what was stored before it was ever edited, breaking the round-trip
 * for every existing one-line value. Multiple paragraphs keep their tags: there is no
 * way to express two blocks without them.
 */
export function fromEditorHtml(html: string): string {
  const withTwig = html.replace(
    ATOM_SPAN,
    (_match, kind: string, payload: string) => {
      const value = unescapeAttribute(payload);
      if (kind === 'var') return `{{ ${value} }}`;

      const raw = value.endsWith('|raw');
      const key = raw ? value.slice(0, -'|raw'.length) : value;
      return `{{ '${key}'|trans${raw ? '|raw' : ''} }}`;
    },
  );

  // TipTap emits `<p></p>` for an empty document; the stored form of "nothing" is ''.
  if (withTwig === '<p></p>') return '';

  const single = SINGLE_PARAGRAPH.exec(withTwig);
  return single && !single[1].includes('<p>') ? single[1] : withTwig;
}
